import apiService from '../utils/apiInterceptor';
import { notification } from 'antd';

/**
 * Service for handling payment-related API calls
 */
const paymentService = {
  /**
   * Mark an order as paid
   * @param {number|string} orderId - Order ID to mark as paid
   * @param {Object} data - Optional payment details
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  markAsPaid: async (orderId, data = {}) => {
    try {
      const requestData = {
        order_id: orderId,
        payment_status: 1,
        ...data
      };
      
      const response = await apiService.post('/change-payment-status', requestData);
      return response;
    } catch (error) {
      console.error(`Error marking order ${orderId} as paid:`, error);
      throw error;
    }
  },
  
  /**
   * Mark an order as unpaid
   * @param {number|string} orderId - Order ID to mark as unpaid
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  markAsUnpaid: async (orderId) => {
    try {
      const requestData = {
        order_id: orderId,
        payment_status: 0
      };
      
      const response = await apiService.post('/change-payment-status', requestData);
      return response;
    } catch (error) {
      console.error(`Error marking order ${orderId} as unpaid:`, error);
      throw error;
    }
  },
  
  /**
   * Mark an order as refunded
   * @param {number|string} orderId - Order ID to mark as refunded
   * @param {Object} data - Optional refund details
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  markAsRefunded: async (orderId, data = {}) => {
    try {
      const requestData = {
        order_id: orderId,
        payment_status: 2,
        refund_reason: data.refund_reason || 'Manual refund',
        ...data
      };
      
      const response = await apiService.post('/change-payment-status', requestData);
      return response;
    } catch (error) {
      console.error(`Error marking order ${orderId} as refunded:`, error);
      throw error;
    }
  },
  
  /**
   * Process a partial refund for an order
   * @param {number|string} orderId - Order ID to partially refund
   * @param {number} amount - Refund amount
   * @param {Object} data - Optional additional refund details
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  markAsPartialRefund: async (orderId, amount, data = {}) => {
    try {
      if (!amount || isNaN(amount) || amount <= 0) {
        throw new Error('Valid refund amount is required');
      }
      
      const requestData = {
        order_id: orderId,
        payment_status: 3,
        refund_amount: amount,
        refund_reason: data.refund_reason || 'Manual partial refund',
        ...data
      };
      
      const response = await apiService.post('/change-payment-status', requestData);
      return response;
    } catch (error) {
      console.error(`Error processing partial refund for order ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Update payment status for multiple orders at once
   * @param {Array} orderIds - Array of order IDs to update
   * @param {string|number} status - Payment status to set (0=Unpaid, 1=Paid, 2=Refunded, 3=Partial Refund)
   * @param {number} refundAmount - Optional refund amount for partial refunds
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updateBulkPaymentStatus: async (orderIds, status, refundAmount = null) => {
    try {
      if (!orderIds || !Array.isArray(orderIds) || orderIds.length === 0) {
        throw new Error('At least one order ID is required');
      }
      
      // Handle partial refund validation
      if (status === '3' || status === 3) {
        if (!refundAmount || isNaN(refundAmount) || refundAmount <= 0) {
          throw new Error('Valid refund amount is required for partial refunds');
        }
      }
      
      const requestData = {
        order_ids: orderIds,
        payment_status: parseInt(status),
      };
      
      // Add refund amount for partial refunds
      if (status === '3' || status === 3) {
        requestData.refund_amount = refundAmount;
        requestData.refund_reason = 'Bulk partial refund';
      }
      
      // For refunds
      if (status === '2' || status === 2) {
        requestData.refund_reason = 'Bulk refund';
      }
      
      const response = await apiService.post('/change-payment-status-bulk', requestData);
      return response;
    } catch (error) {
      console.error('Error updating bulk payment status:', error);
      throw error;
    }
  },
  
  /**
   * Get payment history for an order
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to the payment history
   */
  getPaymentHistory: async (orderId) => {
    try {
      const response = await apiService.get(`/order/${orderId}/payment-history`);
      return response;
    } catch (error) {
      console.error(`Error fetching payment history for order ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Process a manual payment for an order
   * @param {number|string} orderId - Order ID
   * @param {Object} paymentData - Payment details
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  processManualPayment: async (orderId, paymentData) => {
    try {
      const requestData = {
        order_id: orderId,
        ...paymentData
      };
      
      const response = await apiService.post('/process-manual-payment', requestData);
      return response;
    } catch (error) {
      console.error(`Error processing manual payment for order ${orderId}:`, error);
      throw error;
    }
  },

  captureSavedCard: async (orderId) => {
    try {
      return await apiService.post(`/admin/orders/${orderId}/capture-saved-card`);
    } catch (error) {
      console.error(`Error capturing saved card for order ${orderId}:`, error);
      throw new Error(error.response?.data?.message || error.message || 'Unable to charge the saved card.');
    }
  },
  
  /**
   * Get payment statistics for a date range
   * @param {string} startDate - Start date for statistics (YYYY-MM-DD)
   * @param {string} endDate - End date for statistics (YYYY-MM-DD)
   * @returns {Promise<Object>} Promise resolving to payment statistics
   */
  getPaymentStatistics: async (startDate, endDate) => {
    try {
      const response = await apiService.get('/payment-statistics', {
        params: { start_date: startDate, end_date: endDate }
      });
      return response;
    } catch (error) {
      console.error('Error fetching payment statistics:', error);
      throw error;
    }
  },
  
  /**
   * Generate payment report for a date range
   * @param {Object} params - Report parameters
   * @returns {Promise<Object>} Promise resolving to the report data
   */
  generatePaymentReport: async (params) => {
    try {
      const response = await apiService.get('/payment-report', { params });
      return response;
    } catch (error) {
      console.error('Error generating payment report:', error);
      throw error;
    }
  },
  
  /**
   * Send payment reminder to a user
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  sendPaymentReminder: async (orderId) => {
    try {
      const response = await apiService.post(`/send-payment-reminder/${orderId}`);
      return response;
    } catch (error) {
      console.error(`Error sending payment reminder for order ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Send payment reminders for multiple orders
   * @param {Array} orderIds - Array of order IDs
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  sendBulkPaymentReminders: async (orderIds) => {
    try {
      if (!orderIds || !Array.isArray(orderIds) || orderIds.length === 0) {
        throw new Error('At least one order ID is required');
      }
      
      const response = await apiService.post('/send-payment-reminders', {
        order_ids: orderIds
      });
      
      return response;
    } catch (error) {
      console.error('Error sending bulk payment reminders:', error);
      throw error;
    }
  }
};

export default paymentService;
